import {
  Component,
  input,
  OnChanges,
  output,
  SimpleChanges,
} from '@angular/core';
import { AuthTypeValues, DEFAULT_AUTH_VALUES, AuthenticationVersion } from '../../../../types/types';
import { AwTextFormComponent } from '../../../../shared/components/aw-text-form/aw-text-form.component';
import { AuthInputValues } from '../../../../types/types';
import {
  Organization,
  UserService,
} from '../../../../core/services/user.service';

@Component({
  selector: 'app-aw-auth-form',
  standalone: true,
  imports: [AwTextFormComponent],
  templateUrl: './aw-auth-form.component.html',
  styleUrl: './aw-auth-form.component.scss',
})
export class AwAuthFormComponent implements OnChanges {
  constructor(private userService: UserService) {
    this.organization = this.userService.getOrganization();
  }

  authEndpointInput: string | null = null;
  tokenEndpointInput: string | null = null;
  logoutEndpointInput: string | null = null;
  clientIdInput: string | null = null;
  grantType: string | null = null;
  scopeInput: string | null = null;
  extUUIDClaim: string | null = null;
  selfDiscEndpoint: string | null = null;
  tenantId: string | null = null;
  clientSecretValue: string | null = '';

  textForms: any[] = [];

  authType = input<AuthTypeValues | undefined>();
  values = input<AuthInputValues>(DEFAULT_AUTH_VALUES);
  editMode = input<boolean>(false);
  authenticationVersion = input<AuthenticationVersion | undefined>();
  organization: Organization | null = null;

  valuesChangedEvent = output<{
    values: AuthInputValues;
    fieldsFilled: boolean;
    someFieldsFilled: boolean;
  }>();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['values'] && this.values()) {
      this.authEndpointInput = this.values().auth_endpoint;
      this.tokenEndpointInput = this.values().token_endpoint;
      this.logoutEndpointInput = this.values().logout_endpoint ?? '';
      this.clientIdInput = this.values().clientId;
      this.grantType = this.values().grant_type;
      this.scopeInput = this.values().scope;
      this.extUUIDClaim = this.values().ext_userId_claim ?? '';
      this.selfDiscEndpoint = '';
      this.tenantId = this.values().tenantId ?? '';
    }

    this._buildTextForms();

    this.emitCurrentState();
  }

  private _buildTextForms() {
    const DEFAULT_TEXT_FORM = [
      {
        title: 'Authorization Endpoint',
        value: this.authEndpointInput ?? '',
        required: true,
        readOnly: this.organization === 'integration-employee',
        textEntryActionHandler: (text: string | null) =>
          this.onAuthTextEntrySelected(text, 'auth_endpoint'),
      },
      {
        title: 'Token Endpoint',
        value: this.tokenEndpointInput ?? '',
        required: true,
        readOnly: this.organization === 'integration-employee',
        textEntryActionHandler: (text: string | null) =>
          this.onAuthTextEntrySelected(text, 'token_endpoint'),
      },
      {
        title: 'Client ID',
        value: this.clientIdInput ?? '',
        required: true,
        readOnly: this.organization === 'integration-employee',
        textEntryActionHandler: (text: string | null) =>
          this.onAuthTextEntrySelected(text, 'clientId'),
      },
      {
        title: 'Grant Type',
        value: this.authenticationVersion() === 'api' ? 'client_credentials' : 'code',
        required: true,
        readOnly: true,
        textEntryActionHandler: (text: string | null) => {},
      },
      {
        title: 'Client Secret',
        value: this.clientSecretValue ?? '',
        required: this.editMode() ? false : true,
        textEntryActionHandler: (text: string | null) =>
          this.onAuthTextEntrySelected(text, 'client_secret'),
      },
      {
        title: 'Scope',
        value: this.scopeInput ?? '',
        required: true,
        readOnly: this.organization === 'integration-employee',
        textEntryActionHandler: (text: string | null) =>
          this.onAuthTextEntrySelected(text, 'scope'),
      }
    ];
    if (this.authType() === AuthTypeValues.OAUTH2) {
      this.textForms = DEFAULT_TEXT_FORM;
    } else if (this.authType() === AuthTypeValues.ENTRAID) {
      this.textForms = DEFAULT_TEXT_FORM;
      this.textForms.push(
        {
          title: 'Logout Endpoint',
          value: this.logoutEndpointInput ?? '',
          required: true,
          readOnly: this.organization === 'integration-employee',
          textEntryActionHandler: (text: string | null) =>
            this.onAuthTextEntrySelected(text, 'logout_endpoint'),
        },
        {
          title: 'External User ID Claim Name',
          value: this.extUUIDClaim ?? '',
          required: true,
          readOnly: this.organization === 'integration-employee',
          textEntryActionHandler: (text: string | null) =>
            this.onAuthTextEntrySelected(text, 'ext_userId_claim'),
        },
        {
          title: 'Tenant ID',
          value: this.tenantId ?? '',
          required: true,
          readOnly: this.organization === 'integration-employee',
          textEntryActionHandler: (text: string | null) =>
            this.onAuthTextEntrySelected(text, 'tenantId'),
        }
      );
    } else if (this.authType() === AuthTypeValues.OPENIDCONNECT) {
      this.textForms = DEFAULT_TEXT_FORM;
      this.textForms.push(
        {
          title: 'Logout Endpoint',
          value: this.logoutEndpointInput ?? '',
          required: true,
          readOnly: this.organization === 'integration-employee',
          textEntryActionHandler: (text: string | null) =>
            this.onAuthTextEntrySelected(text, 'logout_endpoint'),
        },
        {
          title: 'External User ID Claim Name',
          value: this.extUUIDClaim ?? '',
          required: true,
          readOnly: this.organization === 'integration-employee',
          textEntryActionHandler: (text: string | null) =>
            this.onAuthTextEntrySelected(text, 'ext_userId_claim'),
        }
      );
    }
  }

  private emitCurrentState() {
    const grantType = this.authenticationVersion() === 'api' ? 'client_credentials' : 'code';
    const newValues: AuthInputValues = {
      auth_endpoint: this.authEndpointInput ?? '',
      token_endpoint: this.tokenEndpointInput ?? '',
      logout_endpoint: this.logoutEndpointInput ?? '',
      clientId: this.clientIdInput ?? '',
      client_secret: this.clientSecretValue ?? '',
      grant_type: grantType,
      scope: this.scopeInput ?? '',
      ext_userId_claim: this.extUUIDClaim ?? '',
      self_discovery_endpoint: this.selfDiscEndpoint ?? '',
      tenantId: this.tenantId ?? '',
    };

    let allFilled = false;
    if (this.authType() === AuthTypeValues.OAUTH2)
      allFilled = this._allInputsFilledOAuth();
    else if (this.authType() === AuthTypeValues.ENTRAID)
      allFilled = this._allInputsFilledEntraId();
    else if (this.authType() === AuthTypeValues.OPENIDCONNECT)
      allFilled = this._allInputsFilledOpenId();

    this.valuesChangedEvent.emit({
      values: newValues,
      fieldsFilled: allFilled,
      someFieldsFilled: this._someInputsFilled(),
    });
  }

  private _allInputsFilledOAuth() {
    if (this.editMode()) {
      return !!(
        this.tokenEndpointInput &&
        this.clientIdInput
      );
    } else {
      return !!(
        this.tokenEndpointInput &&
        this.clientIdInput &&
        this.clientSecretValue
      );
    }
  }

  private _allInputsFilledEntraId() {
    if (this.editMode()) {
      return !!(
        this.authEndpointInput &&
        this.tokenEndpointInput &&
        this.logoutEndpointInput &&
        this.clientIdInput &&
        this.extUUIDClaim &&
        this.tenantId
      );
    } else {
      return !!(
        this.authEndpointInput &&
        this.tokenEndpointInput &&
        this.logoutEndpointInput &&
        this.clientIdInput &&
        this.clientSecretValue &&
        this.extUUIDClaim &&
        this.tenantId
      );
    }
  }

  private _allInputsFilledOpenId() {
    if (this.editMode()) {
      return !!(
        this.authEndpointInput &&
        this.tokenEndpointInput &&
        this.logoutEndpointInput &&
        this.clientIdInput
      );
    } else {
      return !!(
        this.authEndpointInput &&
        this.tokenEndpointInput &&
        this.logoutEndpointInput &&
        this.clientIdInput &&
        this.clientSecretValue
      );
    }
  }

  private _someInputsFilled() {
    return !!(
      this.authEndpointInput ||
      this.tokenEndpointInput ||
      this.logoutEndpointInput ||
      this.clientIdInput ||
      this.clientSecretValue ||
      this.scopeInput ||
      this.extUUIDClaim ||
      this.selfDiscEndpoint ||
      this.tenantId
    );
  }

  onAuthTextEntrySelected(text: string | null, key: string) {
    if (key === 'auth_endpoint') {
      this.authEndpointInput = text;
    } else if (key == 'token_endpoint') {
      this.tokenEndpointInput = text;
    } else if (key === 'logout_endpoint') {
      this.logoutEndpointInput = text;
    } else if (key === 'clientId') {
      this.clientIdInput = text;
    } else if (key === 'client_secret') {
      this.clientSecretValue = text;
    } else if (key === 'scope') {
      this.scopeInput = text;
    } else if (key === 'grant_type') {
      this.grantType = text;
    } else if (key === 'ext_userId_claim') {
      this.extUUIDClaim = text;
    } else if (key === 'self_discovery_endpoint') {
      this.selfDiscEndpoint = text;
    } else if (key === 'tenantId') {
      this.tenantId = text;
    }
    this.emitCurrentState();
  }
}
